/**
 * header.js — chrome that lives outside the routed content: the primary nav,
 * the mobile drawer, the search field, and the footer.
 */

import { qs, qsa, esc, apiGet, icon, lockScroll } from '../lib/dom.js';
import { gettext, getLang, localePath, stripLocale, num } from '../lib/i18n.js';

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

/** Build a nested tree from the flat menu list the API returns. */
function buildTree(items) {
  const byId = new Map();
  const roots = [];

  items.forEach((item) => byId.set(item.id, { ...item, children: [] }));
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortByOrder = (a, b) => (a.order || 0) - (b.order || 0);
  roots.sort(sortByOrder);
  roots.forEach((r) => r.children.sort(sortByOrder));
  return roots;
}

/** Menu links are stored unprefixed; prefix them with the active locale. */
function navHref(link) {
  const raw = String(link || '/');
  if (/^(https?:)?\/\//.test(raw) || raw.startsWith('mailto:') || raw.startsWith('tel:')) {
    return raw;
  }
  return localePath(raw);
}

function isActive(link) {
  const current = stripLocale(window.location.pathname).replace(/\/$/, '') || '/';
  const target = stripLocale(String(link || '/')).replace(/\/$/, '') || '/';
  if (target === '/') return current === '/';
  return current === target || current.startsWith(`${target}/`);
}

function renderNavItem(node) {
  const hasChildren = node.children?.length > 0;
  const active = isActive(node.link) ||
    node.children?.some((child) => isActive(child.link));

  return `
    <li class="nv-nav__item ${hasChildren ? 'has-children' : ''} ${active ? 'is-active' : ''}">
      <a href="${esc(navHref(node.link))}" class="nv-nav__link"
         ${hasChildren ? 'aria-haspopup="true" aria-expanded="false"' : ''}>
        <span>${esc(node.title)}</span>
        ${hasChildren ? '<i class="fa-solid fa-chevron-down nv-nav__caret" aria-hidden="true"></i>' : ''}
      </a>
      ${hasChildren ? `
        <ul class="nv-nav__submenu">
          ${node.children.map((child) => `
            <li>
              <a href="${esc(navHref(child.link))}" class="nv-nav__sublink">${esc(child.title)}</a>
            </li>`).join('')}
        </ul>` : ''}
    </li>`;
}

export async function loadNav() {
  const list = qs('#nav-list');
  if (!list) return null;

  let data;
  try {
    data = await apiGet('/api/nav/');
  } catch {
    list.innerHTML = '';
    return null;
  }

  const tree = buildTree(data.menus || []);
  list.innerHTML = tree.map(renderNavItem).join('');

  bindDrawerSubmenus();
  return data;
}

/** On touch, a parent with children expands in place instead of navigating. */
function bindDrawerSubmenus() {
  qsa('.nv-nav__item.has-children > .nv-nav__link').forEach((link) => {
    link.addEventListener('click', (event) => {
      // Only intercept while the drawer layout is active.
      if (!window.matchMedia('(max-width: 61.99rem)').matches) return;
      const item = link.parentElement;
      if (!item.classList.contains('is-expanded')) {
        event.preventDefault();
        item.classList.add('is-expanded');
        link.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/* ------------------------------------------------------------------ *
 * Mobile drawer
 * ------------------------------------------------------------------ */

export function initDrawer() {
  const toggle = qs('#nav-toggle');
  const nav = qs('#main-nav');
  const scrim = qs('#nav-scrim');
  if (!toggle || !nav || !scrim) return;

  const setOpen = (open) => {
    nav.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    scrim.hidden = !open;
    // Next frame so the fade transition runs on first open.
    if (open) requestAnimationFrame(() => scrim.classList.add('is-visible'));
    else scrim.classList.remove('is-visible');
    lockScroll(open);
  };

  toggle.addEventListener('click', () => {
    setOpen(!nav.classList.contains('is-open'));
  });

  scrim.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && nav.classList.contains('is-open')) setOpen(false);
  });

  // Any navigation closes the drawer.
  nav.addEventListener('click', (event) => {
    if (event.target.closest('a') && !event.target.closest('.has-children > .nv-nav__link')) {
      setOpen(false);
    }
  });

  // Returning to desktop width must not leave a stuck scroll lock.
  window.matchMedia('(min-width: 62rem)').addEventListener('change', (e) => {
    if (e.matches) setOpen(false);
  });
}

/* ------------------------------------------------------------------ *
 * Search
 * ------------------------------------------------------------------ */

export function initSearch(navigate) {
  const wrapper = qs('#site-search');
  const toggle = qs('.nv-search__toggle', wrapper);
  const form = qs('#search-form');
  const input = qs('#search-input');
  if (!wrapper || !toggle || !form || !input) return;

  const setOpen = (open) => {
    wrapper.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) setTimeout(() => input.focus(), 60);
  };

  toggle.addEventListener('click', () => setOpen(!wrapper.classList.contains('is-open')));

  document.addEventListener('click', (event) => {
    if (!wrapper.contains(event.target)) setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const query = input.value.trim();
    if (!query) return;
    setOpen(false);
    navigate(localePath(`/search/?q=${encodeURIComponent(query)}`));
  });
}

/* ------------------------------------------------------------------ *
 * Sticky header shadow
 * ------------------------------------------------------------------ */

export function initStickyHeader() {
  const header = qs('#site-header');
  if (!header) return;

  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
  document.body.prepend(sentinel);

  new IntersectionObserver(([entry]) => {
    header.classList.toggle('is-stuck', !entry.isIntersecting);
  }).observe(sentinel);
}

/* ------------------------------------------------------------------ *
 * Language switch — swaps the prefix and keeps the current route.
 * ------------------------------------------------------------------ */

export function initLangSwitch() {
  qsa('[data-lang-link]').forEach((link) => {
    const target = link.dataset.langLink;
    const route = stripLocale(window.location.pathname);
    link.setAttribute('href', `/${target}${route === '/' ? '/' : route}${window.location.search}`);
  });
}

/* ------------------------------------------------------------------ *
 * Footer
 * ------------------------------------------------------------------ */

export async function renderFooter(navData) {
  const host = qs('#footer-content');
  if (!host) return;

  let settings = {};
  try {
    settings = await apiGet('/api/settings/');
  } catch { /* footer degrades to defaults */ }

  const socials = navData?.social_links || [];
  const menus = (navData?.menus || []).filter((m) => !m.parent_id).slice(0, 6);
  const year = new Date().getFullYear();

  const socialMarkup = socials.map((s) => `
    <a href="${esc(s.url)}" class="nv-footer__social" target="_blank" rel="noopener"
       aria-label="${esc(s.title)}" title="${esc(s.title)}">
      ${s.icon_type === 'image' && s.icon_image
        ? `<img src="${esc(s.icon_image)}" alt="">`
        : `<i class="fa-brands ${esc(s.icon_class || 'fa-link')}" aria-hidden="true"></i>`}
    </a>`).join('');

  host.innerHTML = `
    <div class="nv-footer__grid">
      <div class="nv-footer__brand">
        <div style="display: flex; gap: 1rem; align-items: center; margin-block-end: var(--nv-space-4);">
          <img src="${esc(settings.logo || `/static/images/logo_b_${getLang()}.png`)}"
               alt="" class="nv-footer__logo" style="margin-block-end: 0;"
               onerror="this.style.display='none'">
          <picture>
            <source srcset="/static/images/aut_logo_dark.png" media="(prefers-color-scheme: dark)">
            <img src="/static/images/aut_logo_light.png"
                 alt="" class="nv-footer__logo" style="margin-block-end: 0;"
                 onerror="this.style.display='none'">
          </picture>
        </div>
        <p class="nv-footer__about">
          ${esc(settings.footer_text || gettext('The OPEN Innovation Research Lab at Amirkabir University of Technology — from studying local innovation patterns to national development programmes.'))}
        </p>
        ${socialMarkup ? `<div class="nv-footer__socials">${socialMarkup}</div>` : ''}
      </div>

      ${menus.length ? `
      <div>
        <h3 class="nv-footer__heading">${gettext('Quick links')}</h3>
        <ul class="nv-footer__list">
          ${menus.map((m) => `
            <li>
              <a href="${esc(navHref(m.link))}" class="nv-footer__link">
                ${icon('chevron-left', {className: 'nv-icon-flip'})}
                <span>${esc(m.title)}</span>
              </a>
            </li>`).join('')}
        </ul>
      </div>` : ''}

      <div>
        <h3 class="nv-footer__heading">${gettext('Contact')}</h3>
        <ul class="nv-footer__list">
          <li>
            <a href="mailto:info@nobaz.aut.ac.ir" class="nv-footer__link">
              ${icon('envelope', {fixed: true})}
              <span class="nv-ltr">info@nobaz.aut.ac.ir</span>
            </a>
          </li>
          <li>
            <span class="nv-footer__link">
              ${icon('location-dot', {fixed: true})}
              <span>${gettext('Hafez Ave, Amirkabir University of Technology, Tehran')}</span>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div class="nv-footer__bottom">
      <span>
        &copy; <span class="nv-num">${num(year)}</span>
        ${esc(settings.site_name || gettext('NOBaz'))} —
        ${gettext('All rights reserved.')}
      </span>
      ${settings.version
      ? `<span class="nv-footer__version">${gettext('Version')} ${num(settings.version)}</span>`
      : ''}
    </div>`;
}
